import 'App.scss';

import { PaymentProfileTypes } from 'APIs/constants';
import { Industry } from 'APIs/industries/industries.types';
import {
  MerchantMarketplaceSettingsResponse,
  MerchantMarketplaceSettingsUpdateRequest,
} from 'APIs/marketplaces/merchant.marketplaces.settings.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { countries, countryStatesRegions, merchantBusinessTypes } from 'Constants/Constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { json as jsonLang, jsonParseLinter } from '@codemirror/lang-json';
import { linter } from '@codemirror/lint';
import { yupResolver } from '@hookform/resolvers/yup';
import CodeMirror from '@uiw/react-codemirror';

import default_rates from './example-fee-structures/default-rates.json';
import {
  Container,
  EditMerchantSettings,
  FeeStructEditor,
  FeeStructExSelect,
  FormCheckbox,
  FormControl,
  FormItem,
  FormWrapper,
  Label,
  MultiColFormRow,
  SubTitle,
  VInput,
} from './styles';
import {
  MerchantSettingsUpdateFormProps,
  toMerchantMarketplaceSettingsUpdateRequest,
  toMerchantSettingsUpdateFormProps,
} from './types';
import { validationSchema } from './validation.schema';

type Props = {
  taxCountry: string;
  merchantSettings: MerchantMarketplaceSettingsResponse | null;
  isLoading: boolean;
  errorMsg: string;
  industries: Array<Industry>;
  updateMerchantSettings: (data: MerchantMarketplaceSettingsUpdateRequest) => Promise<void>;
  setShowUpdateModal: (showUpdateModal: boolean) => void;
};

const MerchantMarketplaceSettingsForm = ({
  taxCountry,
  merchantSettings,
  isLoading,
  errorMsg,
  industries,
  updateMerchantSettings,
  setShowUpdateModal,
}: Props) => {
  const [hideError, setHideError] = useState<boolean>(false);

  const errorContainerRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MerchantSettingsUpdateFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: merchantSettings ? toMerchantSettingsUpdateFormProps(merchantSettings) : {},
  });

  const onSubmit = useCallback(
    async (data) => {
      const reqData = toMerchantMarketplaceSettingsUpdateRequest(taxCountry, merchantSettings, data);
      updateMerchantSettings(reqData);
    },
    [merchantSettings, taxCountry, updateMerchantSettings],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = useCallback(() => {
    setShowUpdateModal(false);
  }, [setShowUpdateModal]);

  useEffect(() => {
    if (errorMsg && errorContainerRef && errorContainerRef.current) {
      setHideError(false);
      errorContainerRef.current.scrollIntoView();
    }
  }, [errorMsg]);

  const addressCountry = watch('address.country');
  const feeStructExType = watch('feeStructExType');

  const addFeeStruct = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      let json = null;
      switch (feeStructExType) {
        default:
          json = default_rates;
      }
      setValue('feeStructEx', JSON.stringify(json, null, 2));
    },
    [feeStructExType, setValue],
  );

  const resetFeeStruct = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setValue(
        'feeStructEx',
        merchantSettings?.feeStructEx ? JSON.stringify(merchantSettings?.feeStructEx, null, 2) : '',
      );
    },
    [merchantSettings?.feeStructEx, setValue],
  );

  return (
    <Modal
      title="Edit Merchant Marketplace Settings"
      cancelBtnText="Cancel"
      confirmBtnText="Save"
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isLoading}
      isLoading={isLoading}
      large={true}
      testId={'merchant-marketplace-settings-update-modal'}
    >
      <Container>
        <FormWrapper>
          <div ref={errorContainerRef}>
            {errorMsg && !hideError && (
              <Message
                title="Failed to save settings"
                success={false}
                description={errorMsg}
                handleClose={() => setHideError(true)}
              ></Message>
            )}
          </div>
          <FormItem>
            <Label>Business Name*</Label>
            <VInput {...register('businessName')} />
            {errors.businessName && <span className="error-msg">{errors.businessName.message}</span>}
          </FormItem>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Business Type*</Label>
              <FormControl {...register('businessType')}>
                {merchantBusinessTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </FormControl>
              {errors.businessType && <span className="error-msg">{errors.businessType.message}</span>}
            </FormItem>
            <FormItem>
              <Label>Tax ID*</Label>
              <VInput {...register('taxId')} />
              {errors.taxId && <span className="error-msg">{errors.taxId.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <FormItem>
            <Label>Industry*</Label>
            <FormControl {...register('industry')}>
              {[...industries].map((industry) => (
                <option key={industry.name} value={industry.name}>
                  {industry.description}
                </option>
              ))}
            </FormControl>
            {errors.industry && <span className="error-msg">{errors.industry.message}</span>}
          </FormItem>

          <SubTitle>Branding</SubTitle>
          <FormItem>
            <Label>Business Display Name*</Label>
            <VInput {...register('businessDisplayName')} />
            {errors.businessDisplayName && <span className="error-msg">{errors.businessDisplayName.message}</span>}
          </FormItem>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Business Website*</Label>
              <VInput {...register('businessWebsite')} />
              {errors.businessWebsite && <span className="error-msg">{errors.businessWebsite.message}</span>}
            </FormItem>

            <FormItem className="col">
              <Label>Support Email*</Label>
              <VInput {...register('supportEmail')} />
              {errors.supportEmail && <span className="error-msg">{errors.supportEmail.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Icon URI*</Label>
              <VInput {...register('iconUri')} />
              {errors.iconUri && <span className="error-msg">{errors.iconUri.message}</span>}
            </FormItem>

            <FormItem className="col">
              <Label>Logo URI*</Label>
              <VInput {...register('logoUri')} />
              {errors.logoUri && <span className="error-msg">{errors.logoUri.message}</span>}
            </FormItem>
          </MultiColFormRow>
          <FormItem>
            <Label>Logo ALT Text*</Label>
            <VInput {...register('logoAltText')} />
            {errors.logoAltText && <span className="error-msg">{errors.logoAltText.message}</span>}
          </FormItem>

          <SubTitle>Contact details</SubTitle>
          <MultiColFormRow>
            <FormItem className="col">
              <Label>First Name*</Label>
              <VInput {...register('firstName')} />
              {errors.firstName && <span className="error-msg">{errors.firstName.message}</span>}
            </FormItem>

            <FormItem className="col">
              <Label>Last Name*</Label>
              <VInput {...register('lastName')} />
              {errors.lastName && <span className="error-msg">{errors.lastName.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Title*</Label>
              <VInput {...register('title')} />
              {errors.title && <span className="error-msg">{errors.title.message}</span>}
            </FormItem>
            <FormItem className="col">
              <Label>Contact Email*</Label>
              <VInput {...register('contactEmail')} />
              {errors.contactEmail && <span className="error-msg">{errors.contactEmail.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Phone Number*</Label>
              <VInput {...register('phoneNo')} />
              {errors.phoneNo && <span className="error-msg">{errors.phoneNo.message}</span>}
            </FormItem>
            <FormItem className="col"></FormItem>
          </MultiColFormRow>

          <SubTitle>Business address</SubTitle>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Unit Number</Label>
              <VInput {...register('address.unitNumber')} />
              {errors.address?.unitNumber && <span className="error-msg">{errors.address.unitNumber.message}</span>}
            </FormItem>
            <FormItem className="col">
              <Label>Street Number*</Label>
              <VInput {...register('address.streetNumber')} />
              {errors.address?.streetNumber && <span className="error-msg">{errors.address.streetNumber.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Street Name*</Label>
              <VInput {...register('address.streetName')} />
              {errors.address?.streetName && <span className="error-msg">{errors.address.streetName.message}</span>}
            </FormItem>
            <FormItem className="col">
              <Label>Street Type*</Label>
              <VInput {...register('address.streetType')} />
              {errors.address?.streetType && <span className="error-msg">{errors.address.streetType.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <MultiColFormRow>
            <FormItem className="col">
              <Label>Suburb</Label>
              <VInput {...register('address.suburb')} />
              {errors.address?.suburb && <span className="error-msg">{errors.address.suburb.message}</span>}
            </FormItem>
            <FormItem className="col">
              <Label>Postal Code*</Label>
              <VInput {...register('address.postalCode')} type="number" />
              {errors.address?.postalCode && <span className="error-msg">{errors.address.postalCode.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <MultiColFormRow>
            {['AU', 'US'].includes(addressCountry) ? (
              <FormItem className="col">
                <Label>State*</Label>

                <FormControl {...register('address.state')}>
                  <option value="">Select state</option>
                  {countryStatesRegions[addressCountry as keyof typeof countryStatesRegions].map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </FormControl>
                {errors.address?.state && <span className="error-msg">{errors.address.state.message}</span>}
              </FormItem>
            ) : (
              <FormItem className="col">
                <Label>City*</Label>
                {addressCountry === 'NZ' ? (
                  <FormControl {...register('address.city')}>
                    <option value="">Select city</option>
                    {countryStatesRegions.NZ.map((city) => (
                      <option key={city.value} value={city.value}>
                        {city.label}
                      </option>
                    ))}
                  </FormControl>
                ) : (
                  <VInput {...register('address.city')} />
                )}

                {errors.address?.city && <span className="error-msg">{errors.address.city.message}</span>}
              </FormItem>
            )}

            <FormItem className="col">
              <Label>Country*</Label>
              <FormControl {...register('address.country')}>
                {countries.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </FormControl>
              {errors.address?.country && <span className="error-msg">{errors.address.country.message}</span>}
            </FormItem>
          </MultiColFormRow>

          <SubTitle>Fees</SubTitle>
          <MultiColFormRow>
            <FormItem className="col">
              <Label>Calculate Tax On Fee</Label>
              <FormCheckbox type="checkbox" {...register('calculateTaxOnFee')} />
              {errors.calculateTaxOnFee && <span className="error-msg">{errors.calculateTaxOnFee.message}</span>}
            </FormItem>
            <FormItem className="col">
              <Label>Fees Tax Exempt</Label>
              <FormCheckbox type="checkbox" {...register('feesTaxExempt')} />
              {errors.feesTaxExempt && <span className="error-msg">{errors.feesTaxExempt.message}</span>}
            </FormItem>
          </MultiColFormRow>
          <FormItem>
            <Label>Fee Structure</Label>
            <FeeStructExSelect {...register('feeStructExType')}>
              <option key={'default_rates'} value={'default_rates'}>
                {'Default rates'}
              </option>
            </FeeStructExSelect>
            <EditMerchantSettings onClick={addFeeStruct}>{'Apply'}</EditMerchantSettings>
            <EditMerchantSettings onClick={resetFeeStruct}>{'Reset'}</EditMerchantSettings>
            <FeeStructEditor>
              <Controller
                name="feeStructEx"
                control={control}
                render={({ field }) => (
                  <CodeMirror
                    {...field}
                    height="300px"
                    extensions={[jsonLang(), linter(jsonParseLinter())]}
                    onChange={(value: string) => {
                      return field.onChange(value);
                    }}
                  />
                )}
              />
            </FeeStructEditor>
            {errors.feeStructEx && <span className="error-msg">Fee structure data validation failed</span>}
          </FormItem>

          <SubTitle>Pay plan limits</SubTitle>
          <MultiColFormRow>
            <FormItem className="col">
              <Label>Max Pay Plan Amount Unconfirmed Customers*</Label>
              <VInput {...register('maxPayPlanAmountUnconfirmedCustomers')} />
              {errors.maxPayPlanAmountUnconfirmedCustomers && (
                <span className="error-msg">{errors.maxPayPlanAmountUnconfirmedCustomers.message}</span>
              )}
            </FormItem>

            <FormItem className="col">
              <Label>Max Pay Plan Amount Confirmed Customers*</Label>
              <VInput {...register('maxPayPlanAmountConfirmedCustomers')} />
              {errors.maxPayPlanAmountConfirmedCustomers && (
                <span className="error-msg">{errors.maxPayPlanAmountConfirmedCustomers.message}</span>
              )}
            </FormItem>
          </MultiColFormRow>
          <MultiColFormRow>
            <FormItem className="col">
              <Label>Minimum Pay Plan Limit*</Label>
              <VInput {...register('minPlanLimit')} />
              {errors.minPlanLimit && <span className="error-msg">{errors.minPlanLimit.message}</span>}
            </FormItem>
            <FormItem className="col"></FormItem>
          </MultiColFormRow>

          <SubTitle>Financial</SubTitle>
          <FormItem>
            <Label>Payment Profile</Label>
            <FormControl {...register('paymentProfileType')}>
              {Object.keys(PaymentProfileTypes).map((key) => (
                <option key={key} value={key}>
                  {PaymentProfileTypes[key as keyof typeof PaymentProfileTypes]?.label ?? key}
                </option>
              ))}
            </FormControl>
          </FormItem>
        </FormWrapper>
      </Container>
    </Modal>
  );
};

export default MerchantMarketplaceSettingsForm;
